import { AddBoardDialog, useProjects } from "@features/projects";
import type { Board } from "@models/project";
import AccountTreeIcon from "@mui/icons-material/AccountTreeOutlined";
import AddIcon from "@mui/icons-material/AddRounded";
import CableIcon from "@mui/icons-material/CableRounded";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRightRounded";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoardOutlined";
import FactCheckIcon from "@mui/icons-material/FactCheckOutlined";
import HistoryIcon from "@mui/icons-material/HistoryRounded";
import CircuitsIcon from "@mui/icons-material/HubRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ListAltIcon from "@mui/icons-material/ListAltRounded";
import SyncIcon from "@mui/icons-material/SyncRounded";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useOrgStore } from "@stores/orgStore";
import { useProjectStore } from "@stores/projectStore";
import { toast } from "@stores/toastStore";
import { Link as RouterLink, useMatchRoute } from "@tanstack/react-router";
import { type ElementType, useEffect, useMemo, useState } from "react";

interface ProjectSurfaceNavProps {
  projectId: string;
}

/** Routes the sidebar items resolve against. */
type SurfacePath =
  | "/projects/$projectId/overview"
  | "/projects/$projectId/workbench"
  | "/projects/$projectId/chat"
  | "/projects/$projectId/brief"
  | "/projects/$projectId/requirements"
  | "/projects/$projectId/cables"
  | "/projects/$projectId/bom"
  | "/projects/$projectId/pcb"
  | "/projects/$projectId/schematic"
  | "/projects/$projectId/connectors"
  | "/projects/$projectId/circuits"
  | "/projects/$projectId/versions"
  | "/projects/$projectId/sync";

/** Subset of `SurfacePath` that's per-board (silently routes against
 *  the active board today; the route refactor lands later). */
type BoardSurfacePath =
  | "/projects/$projectId/boards/$boardId/schematic"
  | "/projects/$projectId/boards/$boardId/pcb"
  | "/projects/$projectId/boards/$boardId/bom"
  | "/projects/$projectId/boards/$boardId/connectors"
  | "/projects/$projectId/boards/$boardId/circuits";

interface SurfaceItem {
  label: string;
  to: SurfacePath;
  icon: ElementType;
}

interface BoardSurfaceItem {
  label: string;
  to: BoardSurfacePath;
  icon: ElementType;
}

/**
 * Project navigation — Workbench-led IA per `docs/ux-redesign/`.
 *
 *   Overview                                ← landing (no cluster header)
 *   Workbench                               ← AI artefact home
 *   Chat                                    ← conversation surface
 *
 *   project     — Brief · Cables (N≥2)
 *   boards      — per-board expandable groups · + Add board
 *
 *   ─
 *   Plugins · Versions                      ← bottom (no cluster header)
 *
 * Boards are rendered as one expandable group per Board so multi-
 * board projects are navigable from the sidebar; collapsing all but
 * one keeps the visible footprint small. Single-board projects open
 * their only group by default — no UX cliff between N=1 and N=2.
 *
 * "+ Add board" is always visible at the bottom of the boards group
 * regardless of count. Solves the chicken-and-egg the previous picker
 * had where you couldn't add a second board until you already had
 * one.
 *
 * Rows are real `<a href>` links via TanStack Router's `Link` so
 * cmd/ctrl-click and middle-click open in a new tab. The
 * `activeBoardId` store mirror is set by `ProjectLayoutPage` from the
 * URL on mount — no imperative setter needed in click handlers.
 */
const TOP_ITEMS: SurfaceItem[] = [
  {
    label: "Overview",
    to: "/projects/$projectId/overview",
    icon: InfoOutlinedIcon,
  },
  {
    label: "Workbench",
    to: "/projects/$projectId/workbench",
    icon: WorkspacesOutlinedIcon,
  },
  {
    label: "Chat",
    to: "/projects/$projectId/chat",
    icon: ChatBubbleOutlineIcon,
  },
];

const PROJECT_CLUSTER_ITEMS: SurfaceItem[] = [
  {
    label: "Brief",
    to: "/projects/$projectId/brief",
    icon: DescriptionIcon,
  },
  {
    // Sits between Brief (free-text intent) and Cables (structured
    // multi-board linkage) — Requirements is the structured surface
    // for what Brief describes in prose. Same project-scope as Brief.
    label: "Requirements",
    to: "/projects/$projectId/requirements",
    icon: FactCheckIcon,
  },
  {
    label: "Cables",
    to: "/projects/$projectId/cables",
    icon: CableIcon,
  },
];

const BOARD_SURFACES: BoardSurfaceItem[] = [
  {
    label: "Schematic",
    to: "/projects/$projectId/boards/$boardId/schematic",
    icon: AccountTreeIcon,
  },
  {
    label: "PCB",
    to: "/projects/$projectId/boards/$boardId/pcb",
    icon: DeveloperBoardIcon,
  },
  {
    label: "BOM",
    to: "/projects/$projectId/boards/$boardId/bom",
    icon: ListAltIcon,
  },
  {
    label: "Connectors",
    to: "/projects/$projectId/boards/$boardId/connectors",
    icon: CableIcon,
  },
  {
    label: "Circuits",
    to: "/projects/$projectId/boards/$boardId/circuits",
    icon: CircuitsIcon,
  },
];

const BOTTOM_ITEMS: SurfaceItem[] = [
  {
    label: "Plugins",
    to: "/projects/$projectId/sync",
    icon: SyncIcon,
  },
  {
    label: "Versions",
    to: "/projects/$projectId/versions",
    icon: HistoryIcon,
  },
];

export function ProjectSurfaceNav({ projectId }: ProjectSurfaceNavProps) {
  const matchRoute = useMatchRoute();
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  const activeBoardId = useProjectStore((s) => s.activeBoardId);
  const setActiveBoard = useProjectStore((s) => s.setActiveBoard);
  const { data: allProjects } = useProjects(activeOrgId ?? "personal");

  // Resolve the wrapper Project from the cached list. Same matching
  // rules as ProjectBoardPicker — scope the lookup by activeOrgId
  // when set, fall back to the first by name.
  const projectRow = useMemo(() => {
    if (!allProjects) return null;
    const matches = allProjects.filter((p) => p.id === projectId);
    if (matches.length === 0) return null;
    if (activeOrgId) {
      return (
        matches.find((p) => p.organisation_id === activeOrgId) ?? matches[0]
      );
    }
    return matches.find((p) => !p.organisation_id) ?? matches[0];
  }, [allProjects, projectId, activeOrgId]);

  const boards: Board[] = useMemo(() => {
    if (!projectRow?.boards) return [];
    return [...projectRow.boards].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true }),
    );
  }, [projectRow]);

  const boardCount = boards.length;

  // Default the active board to the first one when none is selected
  // — ensures per-board surfaces have a board to render against
  // immediately after the sidebar mounts.
  useEffect(() => {
    if (boards.length === 0) return;
    if (activeBoardId && boards.some((b) => b.id === activeBoardId)) return;
    setActiveBoard(boards[0].id);
  }, [boards, activeBoardId, setActiveBoard]);

  // Cables only meaningful at N≥2 boards. Filter at render time so
  // adding a second board makes Cables appear immediately.
  const projectItems = useMemo(
    () =>
      PROJECT_CLUSTER_ITEMS.filter(
        (item) =>
          !(item.to === "/projects/$projectId/cables" && boardCount < 2),
      ),
    [boardCount],
  );

  // Add-board dialog state — owned here so the "+ Add board" affordance
  // can sit inline with the boards group.
  const [addBoardOpen, setAddBoardOpen] = useState(false);

  // Per-board expansion state. The active board defaults to expanded;
  // clicking a different board's chevron expands it (collapsing the
  // previously-expanded one — the Linear pattern).
  const [expandedBoardId, setExpandedBoardId] = useState<string | null>(null);

  // Auto-expand the active board on first mount / when it changes.
  // Intentionally re-runs only on activeBoardId changes — adding
  // expandedBoardId to deps would re-snap the expansion every time
  // the user expands a different board manually.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
  useEffect(() => {
    if (activeBoardId && expandedBoardId !== activeBoardId) {
      setExpandedBoardId(activeBoardId);
    }
  }, [activeBoardId]);

  const renderTopItem = (item: SurfaceItem) => {
    const { label, to, icon: Icon } = item;
    const active = !!matchRoute({ to, params: { projectId } });
    const href = to.replace("$projectId", projectId);
    return (
      <ListItemButton
        key={to}
        component={RouterLink}
        to={href}
        aria-current={active ? "page" : undefined}
        disableRipple
        sx={(t) => ({
          py: 0.875,
          px: 1.25,
          gap: 1,
          borderRadius: 1,
          position: "relative",
          overflow: "hidden",
          textDecoration: "none",
          color: active ? "primary.light" : "text.secondary",
          bgcolor: active ? alpha(t.palette.primary.main, 0.1) : "transparent",
          transition: `background-color ${t.motion.fast}, color ${t.motion.fast}, box-shadow ${t.motion.fast}`,
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            borderRadius: "inherit",
            boxShadow: active
              ? `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.42)}`
              : "none",
            transition: `box-shadow ${t.motion.fast}`,
          },
          "&:hover": {
            bgcolor: alpha(t.palette.primary.main, active ? 0.16 : 0.06),
            color: active ? "primary.light" : "text.primary",
          },
          "&:hover::before": {
            boxShadow: active
              ? `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.6)}`
              : `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.18)}`,
          },
          "&.Mui-selected, &.Mui-selected:hover": {
            bgcolor: "inherit",
          },
        })}
      >
        <ListItemIcon sx={{ minWidth: 0, color: "inherit" }}>
          <Icon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            fontSize: "0.875rem",
            fontWeight: active ? 600 : 500,
            letterSpacing: "-0.005em",
          }}
        />
        {active && (
          <Box
            aria-hidden
            sx={(t) => ({
              width: 5,
              height: 5,
              borderRadius: "50%",
              bgcolor: "primary.main",
              boxShadow: `0 0 0 2px ${alpha(t.palette.primary.main, 0.18)}`,
              flexShrink: 0,
            })}
          />
        )}
      </ListItemButton>
    );
  };

  const renderBoardGroup = (board: Board) => {
    const isActiveBoard = board.id === activeBoardId;
    const isExpanded = expandedBoardId === board.id;
    // Active when we're on this board's overview page itself.
    const onOverview = !!matchRoute({
      to: "/projects/$projectId/boards/$boardId",
      params: { projectId, boardId: board.id },
    });
    return (
      <Box key={board.id}>
        <ListItemButton
          component={RouterLink}
          to={`/projects/${projectId}/boards/${board.id}`}
          onClick={() => setExpandedBoardId(board.id)}
          aria-current={onOverview ? "page" : undefined}
          disableRipple
          sx={(t) => ({
            py: 0.7,
            px: 1.25,
            gap: 1,
            borderRadius: 1,
            position: "relative",
            overflow: "hidden",
            textDecoration: "none",
            color: "inherit",
            bgcolor: onOverview
              ? alpha(t.palette.primary.main, 0.1)
              : isActiveBoard
                ? alpha(t.palette.primary.main, 0.05)
                : "transparent",
            transition: `background-color ${t.motion.fast}, box-shadow ${t.motion.fast}`,
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              borderRadius: "inherit",
              boxShadow: onOverview
                ? `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.42)}`
                : "none",
              transition: `box-shadow ${t.motion.fast}`,
            },
            "&:hover": {
              bgcolor: alpha(t.palette.primary.main, onOverview ? 0.16 : 0.06),
            },
            "&:hover::before": {
              boxShadow: onOverview
                ? `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.6)}`
                : `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.18)}`,
            },
            "&.Mui-selected, &.Mui-selected:hover": { bgcolor: "inherit" },
          })}
        >
          {/* Chevron toggles expansion only. preventDefault stops the
              row's anchor navigation; stopPropagation keeps the click
              from bubbling. Lets the user expand a board to scan its
              surfaces without committing to opening it. */}
          <Box
            component="button"
            type="button"
            aria-label={isExpanded ? "Collapse board" : "Expand board"}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setExpandedBoardId(isExpanded ? null : board.id);
            }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 18,
              height: 18,
              padding: 0,
              border: "none",
              background: "transparent",
              borderRadius: 0.5,
              flexShrink: 0,
              cursor: "pointer",
              color: "inherit",
              "&:hover": {
                color: "text.primary",
              },
            }}
          >
            <ChevronRightIcon
              sx={{
                fontSize: 16,
                color: "text.disabled",
                transition: (t) => `transform ${t.motion?.fast ?? "120ms"}`,
                transform: isExpanded ? "rotate(90deg)" : "none",
              }}
            />
          </Box>
          <Typography
            sx={(t) => ({
              fontFamily: t.fonts?.mono ?? "monospace",
              fontSize: "0.8125rem",
              fontWeight: isActiveBoard ? 600 : 500,
              color: isActiveBoard ? "text.primary" : "text.secondary",
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            })}
          >
            {board.name}
          </Typography>
          <Box
            sx={{
              ml: "auto",
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flexShrink: 0,
            }}
          >
            {board.current_version_number != null ? (
              <Typography
                variant="caption"
                sx={(t) => ({
                  fontFamily: t.fonts?.mono ?? "monospace",
                  fontSize: "0.6875rem",
                  color: "text.secondary",
                  letterSpacing: "0.04em",
                })}
              >
                v{board.current_version_number}
              </Typography>
            ) : (
              <Typography
                variant="caption"
                sx={(t) => ({
                  fontFamily: t.fonts?.mono ?? "monospace",
                  fontSize: "0.6875rem",
                  color: "text.secondary",
                  letterSpacing: "0.02em",
                  fontStyle: "italic",
                  opacity: 0.6,
                })}
              >
                no netlist
              </Typography>
            )}
            {typeof board.component_count === "number" && (
              <Typography
                variant="caption"
                sx={(t) => ({
                  fontFamily: t.fonts?.mono ?? "monospace",
                  fontSize: "0.6875rem",
                  color: "text.secondary",
                })}
              >
                {board.component_count}
              </Typography>
            )}
          </Box>
        </ListItemButton>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List
            disablePadding
            sx={{ display: "flex", flexDirection: "column", gap: "1px" }}
          >
            {BOARD_SURFACES.map((item) => {
              // Routes are board-scoped (`/boards/$boardId/$surface`),
              // so a match implicitly means this row's board is the
              // one in the URL — no separate "active board" check
              // needed beyond the route match.
              const active = !!matchRoute({
                to: item.to,
                params: { projectId, boardId: board.id },
              });
              const Icon = item.icon;
              return (
                <ListItemButton
                  key={item.to}
                  component={RouterLink}
                  to={item.to
                    .replace("$projectId", projectId)
                    .replace("$boardId", board.id)}
                  aria-current={active ? "page" : undefined}
                  disableRipple
                  sx={(t) => ({
                    py: 0.6,
                    pl: 4.25,
                    pr: 1.25,
                    gap: 1,
                    borderRadius: 1,
                    position: "relative",
                    overflow: "hidden",
                    textDecoration: "none",
                    color: active ? "primary.light" : "text.secondary",
                    bgcolor: active
                      ? alpha(t.palette.primary.main, 0.1)
                      : "transparent",
                    transition: `background-color ${t.motion.fast}, color ${t.motion.fast}, box-shadow ${t.motion.fast}`,
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      pointerEvents: "none",
                      borderRadius: "inherit",
                      boxShadow: active
                        ? `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.42)}`
                        : "none",
                      transition: `box-shadow ${t.motion.fast}`,
                    },
                    "&:hover": {
                      bgcolor: alpha(
                        t.palette.primary.main,
                        active ? 0.16 : 0.06,
                      ),
                      color: active ? "primary.light" : "text.primary",
                    },
                    "&:hover::before": {
                      boxShadow: active
                        ? `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.6)}`
                        : `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.18)}`,
                    },
                    "&.Mui-selected, &.Mui-selected:hover": {
                      bgcolor: "inherit",
                    },
                  })}
                >
                  <ListItemIcon sx={{ minWidth: 24, color: "inherit" }}>
                    <Icon sx={{ fontSize: 16 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: "0.8125rem",
                      fontWeight: active ? 600 : 500,
                      letterSpacing: "-0.005em",
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Collapse>
      </Box>
    );
  };

  const renderTopList = (items: SurfaceItem[]) => (
    <List
      disablePadding
      sx={{ display: "flex", flexDirection: "column", gap: "2px" }}
    >
      {items.map(renderTopItem)}
    </List>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/* Top-level destinations: Overview, Workbench, Chat. */}
      {renderTopList(TOP_ITEMS)}

      {/* project — Brief, Cables (N≥2). */}
      <Box>
        <ClusterHeader label="project" />
        {renderTopList(projectItems)}
      </Box>

      {/* boards — per-board expandable groups + Add board. */}
      <Box>
        <ClusterHeader label="boards" />
        <List
          disablePadding
          sx={{ display: "flex", flexDirection: "column", gap: "2px" }}
        >
          {boards.map(renderBoardGroup)}
          <ListItemButton
            onClick={() => setAddBoardOpen(true)}
            disableRipple
            sx={(t) => ({
              py: 0.7,
              px: 1.25,
              gap: 1,
              borderRadius: 1,
              border: "1px dashed",
              borderColor: alpha(t.palette.primary.main, 0.25),
              bgcolor: "transparent",
              mt: boards.length > 0 ? 0.5 : 0,
              color: "text.secondary",
              transition: `border-color ${t.motion.fast}, color ${t.motion.fast}, background-color ${t.motion.fast}`,
              "&:hover": {
                borderColor: alpha(t.palette.primary.main, 0.5),
                color: "primary.light",
                bgcolor: alpha(t.palette.primary.main, 0.06),
              },
            })}
          >
            <ListItemIcon sx={{ minWidth: 24 }}>
              <AddIcon sx={{ fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText
              primary="Add board"
              primaryTypographyProps={{
                fontSize: "0.8125rem",
                fontWeight: 500,
              }}
            />
          </ListItemButton>
        </List>
      </Box>

      {/* Bottom-level: Plugins · Versions. Separator above so
          they read as distinct from the design surfaces. */}
      <Box>
        <Box
          sx={(t) => ({
            height: "1px",
            background: t.palette.border?.default ?? t.palette.divider,
            mx: 1.25,
            mb: 0.75,
          })}
        />
        {renderTopList(BOTTOM_ITEMS)}
      </Box>

      <AddBoardDialog
        open={addBoardOpen}
        onClose={() => setAddBoardOpen(false)}
        projectId={projectId}
        boards={boards}
        projectName={projectRow?.name}
        organisationId={projectRow?.organisation_id ?? null}
        onAdded={(board) => {
          toast.success(
            `Added '${board.name}' to ${projectRow?.name ?? "project"}`,
          );
          // Snap the sidebar to the freshly-added board so the user
          // immediately sees its surfaces in the nav.
          setActiveBoard(board.id);
        }}
      />
    </Box>
  );
}

function ClusterHeader({ label }: { label: string }) {
  return (
    <Typography
      sx={(t) => ({
        fontFamily: t.fonts.mono,
        fontSize: "0.625rem",
        fontWeight: 500,
        color: "text.secondary",
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        px: 1.25,
        mb: 0.5,
        lineHeight: 1,
      })}
    >
      {label}
    </Typography>
  );
}
